import { Injectable } from '@angular/core';
import { RepositoryService } from './repository.service';
import { LfTreeNode, LfTreeNodePage, LfTreeNodeService } from '@laserfiche/lf-ui-components/lf-repository-browser';

const FOLDER_SVG: string = 'https://lfxstatic.com/Site/lf-web-components/beta/folder.svg';
const FILE_SVG: string = 'https://lfxstatic.com/Site/lf-web-components/beta/file.svg';
/**
 * Database for dynamic data. When expanding a node in the tree, the data source will need to fetch
 * the descendants data from the database.
 */

 export interface EntryTreeNode extends LfTreeNode {
  entryType: string;
  level: number;
 }

 export enum EntryType {
   Document = 'Document',
   Shortcut = 'Shortcut',
   Folder = 'Folder',
   RecordSeries = 'RecordSeries'
 }

@Injectable({ providedIn: 'root' })
export class LfRepoTreeService implements LfTreeNodeService {
  repoId: string;
  sourceFolder: string = '1';

  filterString: string = '';

  constructor(private repositoryService: RepositoryService) 
  { }
  
  public setRepositoryId(repositoryId){
    this.repoId = repositoryId;
  }

  public setSourceFolder(source) {
    if (source) {
      this.sourceFolder = source;
    }
  }
  
  async getFolderChildrenAsync(folder: LfTreeNode, nextPage?: string): Promise<LfTreeNodePage> {
    let dataMap: EntryTreeNode[] = [];
    await this.repositoryService.getRepositoryEntryListing(this.repoId, folder.id, []).toPromise().then(
      result => {
        for (const entry of result['data']) {

          const regex = new RegExp(this.filterString, "i");
          if (!entry.name.match(regex)) { continue; }

          let metaData = {
            name: entry.name,
            path: entry.props.FullPath,
            entryType: entry.props.EntryType,
            id: entry.props.Id,
          }

          if (entry.props.EntryType == EntryType.Folder) {
            dataMap.push(this.createFolderNode(metaData));
          } else {
            if (entry.props.EntryType == EntryType.Document){
              dataMap.push(this.createLeafNode(metaData));
            }
          }
        }
      }
    );

    if (dataMap){
      dataMap = this.sortTreeNodes(dataMap);
    } 

    return {
      page: dataMap,
      nextPage: undefined
    };
  }
  async getRootTreeNodeAsync(): Promise<LfTreeNode> {
    return await this.getTreeNodeByIdAsync(this.sourceFolder);
  }
  async getParentTreeNodeAsync(treeNode: LfTreeNode): Promise<LfTreeNode> {
    return undefined;
  }
  async getTreeNodeByIdAsync(id: string): Promise<LfTreeNode> {
    let node: EntryTreeNode = undefined;

    await this.repositoryService.getRepositoryEntryProp(this.repoId, id).toPromise().then(
      result => {
        let entryProp = result['data'].props;
        let metadata = {
          path: entryProp.FullPath,
          id: id,
          name: '',
          entryType: EntryType.Folder
        }
        if (id == "1"){
          metadata.name = entryProp.Repository;
          node = this.createFolderNode(metadata);
        } else {
          metadata.name = entryProp.Name;
          node = this.createFolderNode(metadata);
        }
      }
    )
    
    return node;
  }

  async refreshAsync(node: EntryTreeNode): Promise<LfTreeNodePage> {
    return await this.getFolderChildrenAsync(node);
  }

  sortTreeNodes(nodes: EntryTreeNode[]){
    let folderNodes = nodes.filter(node => node.entryType == EntryType.Folder);
    let docNodes = nodes.filter(node => node.entryType == EntryType.Document);

    return folderNodes.concat(docNodes);
  }

  private createFolderNode(metadata): EntryTreeNode {
    const pathArray = metadata.path.split('/');
    const folderNode: EntryTreeNode = {
      id: metadata.id,
      name: metadata.name,
      path: metadata.path,
      entryType: metadata.entryType,
      icon: FOLDER_SVG,
      isContainer: true,
      level: pathArray.length,
      isLeaf: false
    };
    return folderNode;
  }

  private createLeafNode(metadata): EntryTreeNode {
    const pathArray = metadata.path.split('/');
    const leafNode: EntryTreeNode = {
      id: metadata.id,
      name: metadata.name,
      path: metadata.path,
      entryType: metadata.entryType,
      icon: FILE_SVG,
      isContainer: false,
      level: pathArray.length,
      isLeaf: true
    };
    return leafNode;
  }
}
